/* Complete-the-kit block (PDP) — 2026-08-18
   Adds the current product + checked companions to the cart in one request,
   mirrors the expected automatic-discount % in the total, and opens the drawer. */
if (!customElements.get('kit-block')) {
  class KitBlock extends HTMLElement {
    connectedCallback() {
      this.triggerVariantId = this.dataset.triggerVariant;
      this.triggerPrice = parseInt(this.dataset.triggerPrice, 10) || 0;
      this.triggerAvailable = this.dataset.triggerAvailable === 'true';
      this.items = Array.from(this.querySelectorAll('[data-kit-item]'));
      this.button = this.querySelector('[data-kit-add]');
      this.btnText = this.querySelector('[data-kit-btn-text]');
      this.err = this.querySelector('[data-kit-error]');

      this.items.forEach((row) => row.querySelector('.kit__input').addEventListener('change', () => this.update()));
      this.button.addEventListener('click', this.add.bind(this));

      // Edition picker on the trigger tile: drive the main variant picker so price, media,
      // sticky form and this block all stay in sync. Falls back to a local update if no radio exists.
      this.select = this.querySelector('[data-kit-trigger-select]');
      if (this.select) {
        this.select.addEventListener('change', () => {
          const opt = this.select.selectedOptions[0];
          if (!opt) return;
          const radio = document.querySelector(`input[type="radio"][form="${this.dataset.formId}"][data-option-value="${CSS.escape(opt.dataset.optionValue)}"]`);
          if (radio && !radio.checked) { radio.click(); return; }
          this.triggerVariantId = opt.value;
          this.triggerPrice = parseInt(opt.dataset.price, 10) || 0;
          this.triggerAvailable = opt.dataset.available === 'true';
          const p = this.querySelector('[data-kit-trigger-price]'); if (p) p.innerHTML = this.money(this.triggerPrice);
          const c = this.querySelector('[data-kit-trigger-compare]'); if (c) { const cmp = parseInt(opt.dataset.compare, 10) || 0; const show = cmp > this.triggerPrice; c.hidden = !show; if (show) c.innerHTML = this.money(cmp); }
          this.update();
        });
      }

      // Follow the main variant picker (Disc/Digital etc.) so price + id stay in sync.
      if (window.theme && theme.pubsub) {
        this.unsub = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (e) => {
          const v = e?.data?.variant;
          if (!v || String(e.data.productId) !== this.dataset.productId) return;
          this.triggerVariantId = v.id;
          this.triggerPrice = v.price;
          this.triggerAvailable = !!v.available;
          const t = this.querySelector('[data-kit-trigger-title]'); if (t) { t.textContent = v.title; t.hidden = v.title === 'Default Title'; }
          if (this.select && String(this.select.value) !== String(v.id)) this.select.value = String(v.id);
          const p = this.querySelector('[data-kit-trigger-price]'); if (p) p.innerHTML = this.money(v.price);
          const c = this.querySelector('[data-kit-trigger-compare]'); if (c) { const show = v.compare_at_price && v.compare_at_price > v.price; c.hidden = !show; if (show) c.innerHTML = this.money(v.compare_at_price); }
          this.update();
        });
      }
      this.update();
    }
    disconnectedCallback() { if (this.unsub) this.unsub(); }

    money(cents) {
      const fmt = (window.theme && theme.settings && theme.settings.moneyFormat) || (window.themeVariables && themeVariables.settings.moneyFormat) || '${{amount}}';
      return theme.Currency.formatMoney(cents, fmt);
    }

    get checked() { return this.items.filter((r) => r.querySelector('.kit__input').checked); }

    update() {
      const on = this.checked;
      this.items.forEach((r) => r.classList.toggle('is-off', !r.querySelector('.kit__input').checked));
      const count = on.length + 1;
      const kitSize = parseInt(this.dataset.kitSize, 10) || 3;
      const full = parseFloat(this.dataset.discountFull) || 0;
      const partial = parseFloat(this.dataset.discountPartial) || 0;
      const sub = this.triggerPrice + on.reduce((s, r) => s + (parseInt(r.dataset.price, 10) || 0), 0);
      const pct = count >= kitSize ? full : (count >= 2 ? partial : 0);
      const total = Math.round(sub * (1 - pct / 100));
      const saving = sub - total;

      this.querySelector('[data-kit-total]').innerHTML = this.money(total);
      const was = this.querySelector('[data-kit-was]');
      was.hidden = !saving; if (saving) was.innerHTML = this.money(sub);
      const sv = this.querySelector('[data-kit-saving]');
      if (sv) { sv.hidden = !saving; if (saving) sv.innerHTML = (this.dataset.labelSave || 'save [save] ([pct]%)').replace('[save]', this.money(saving)).replace('[pct]', pct); }

      let label;
      if (count >= kitSize) label = this.dataset.labelAdd.replace('[save]', this.money(saving).replace(/<[^>]+>/g, ''));
      else label = this.dataset.labelAddSome.replace('[n]', count);
      this.btnText.textContent = label;
      this.button.toggleAttribute('aria-disabled', !this.triggerAvailable);
    }

    async add() {
      if (this.button.hasAttribute('aria-disabled') || this.button.hasAttribute('aria-busy')) return;
      this.err.hidden = true;
      const items = [{ id: parseInt(this.triggerVariantId, 10), quantity: 1 }]
        .concat(this.checked.map((r) => ({ id: parseInt(r.dataset.variantId, 10), quantity: 1 })));

      if (document.body.classList.contains('template-cart') || (theme.settings && theme.settings.cartType === 'page')) {
        if (!window.Shopify || !window.Shopify.designMode) theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { items } });
        return;
      }

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
      this.button.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(theme.routes.cart_add_url, {
          ...theme.utils.fetchConfig('javascript'),
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname })
        });
        const parsed = await res.json();
        if (parsed.status) {
          this.err.textContent = parsed.description || parsed.message || 'Could not add the kit. Please try again.';
          this.err.hidden = false;
          return;
        }
        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson.sections = parsed.sections;
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'kit-block', productVariantId: this.triggerVariantId, cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsed } }));
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'kit_add_to_cart', kit_items: items.length, kit_product_id: this.dataset.productId });
        document.querySelector('cart-drawer')?.show(this.button);
      } catch (e) {
        console.error(e);
        this.err.textContent = 'Could not add the kit. Please try again.';
        this.err.hidden = false;
      } finally {
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('kit-block', KitBlock);
}
